import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Exporta datos tabulares a Excel
 * @param {Array} data - Array de objetos con los datos
 * @param {string} filename - Nombre del archivo sin extensión
 * @param {string} sheetName - Nombre de la hoja de Excel
 */
export const exportToExcel = (data, filename = 'reporte', sheetName = 'Datos') => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

/**
 * Exporta una tabla a PDF
 * @param {Array} headers - Array de strings con los encabezados
 * @param {Array} rows - Array de arrays con los datos de las filas
 * @param {string} title - Título del documento
 * @param {string} filename - Nombre del archivo sin extensión
 */
export const exportToPDF = (headers, rows, title = 'Reporte', filename = 'reporte') => {
  const doc = new jsPDF();
  
  // Título
  doc.setFontSize(18);
  doc.text(title, 14, 22);
  
  // Fecha
  doc.setFontSize(11);
  doc.text(`Generado: ${new Date().toLocaleString('es-GT')}`, 14, 30);
  
  // Tabla
  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: 35,
    styles: {
      fontSize: 9,
      cellPadding: 2,
    },
    headStyles: {
      fillColor: [0, 188, 212], // cyan
      textColor: 255,
      fontStyle: 'bold'
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245]
    }
  });
  
  doc.save(`${filename}.pdf`);
};

/**
 * Exporta reporte anual matricial a Excel
 * @param {Object} reportData - Datos del reporte con months, categories/suppliers, data, totals
 * @param {string} filename - Nombre del archivo
 * @param {string} type - 'categories' o 'suppliers'
 */
export const exportAnnualReportToExcel = (reportData, filename = 'reporte-anual', type = 'categories') => {
  const { months, categories, suppliers, monthly_totals, grand_total } = reportData;
  
  // Preparar datos para Excel
  const excelData = [];
  
  // Encabezados
  const headers = [type === 'categories' ? 'Categoría' : 'Proveedor'];
  if (type === 'suppliers') headers.push('NIT');
  headers.push(...months, 'Total', '%');
  
  excelData.push(headers);
  
  // Datos
  const items = type === 'categories' ? (categories || []) : (suppliers || []);
  
  if (items.length === 0) {
    // Si no hay datos, agregar fila vacía
    const emptyRow = ['Sin datos'];
    if (type === 'suppliers') emptyRow.push('');
    months.forEach(() => emptyRow.push(0));
    emptyRow.push(0, '0%');
    excelData.push(emptyRow);
  } else {
    items.forEach((item) => {
      const row = [item.category || item.name || item.supplier_name];
      if (type === 'suppliers') row.push(item.supplier_nit || '');
      
      // Los datos mensuales están en item.months
      const monthlyData = item.months || [];
      monthlyData.forEach((amount) => {
        row.push(amount || 0);
      });
      
      row.push(item.total || 0, (item.percentage || 0) + '%');
      excelData.push(row);
    });
  }
  
  // Totales
  const totalRow = ['TOTAL'];
  if (type === 'suppliers') totalRow.push('');
  totalRow.push(...(monthly_totals || months.map(() => 0)), grand_total || 0, '100%');
  excelData.push(totalRow);
  
  // Crear libro de Excel
  const worksheet = XLSX.utils.aoa_to_sheet(excelData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Reporte');
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

/**
 * Exporta reporte anual matricial a PDF
 * @param {Object} reportData - Datos del reporte
 * @param {string} title - Título del reporte
 * @param {string} filename - Nombre del archivo
 * @param {string} type - 'categories' o 'suppliers'
 */
export const exportAnnualReportToPDF = (reportData, title, filename = 'reporte-anual', type = 'categories') => {
  const { months, categories, suppliers, monthly_totals, grand_total } = reportData;
  
  const doc = new jsPDF('landscape'); // Modo horizontal para tablas anchas
  
  // Título
  doc.setFontSize(16);
  doc.text(title, 14, 15);
  
  // Fecha
  doc.setFontSize(10);
  doc.text(`Generado: ${new Date().toLocaleString('es-GT')}`, 14, 22);
  
  // Preparar encabezados
  const headers = [type === 'categories' ? 'Categoría' : 'Proveedor'];
  if (type === 'suppliers') headers.push('NIT');
  headers.push(...months, 'Total', '%');
  
  // Preparar filas
  const rows = [];
  const items = type === 'categories' ? (categories || []) : (suppliers || []);
  
  if (items.length === 0) {
    // Si no hay datos
    const emptyRow = ['Sin datos'];
    if (type === 'suppliers') emptyRow.push('');
    months.forEach(() => emptyRow.push('Q0.00'));
    emptyRow.push('Q0.00', '0%');
    rows.push(emptyRow);
  } else {
    items.forEach((item) => {
      const row = [item.category || item.name || item.supplier_name];
      if (type === 'suppliers') row.push(item.supplier_nit || '');
      
      // Los datos mensuales están en item.months
      const monthlyData = item.months || [];
      monthlyData.forEach((amount) => {
        row.push(`Q${(amount || 0).toFixed(2)}`);
      });
      
      row.push(`Q${(item.total || 0).toFixed(2)}`, `${(item.percentage || 0).toFixed(1)}%`);
      rows.push(row);
    });
  }
  
  // Fila de totales
  const totalRow = ['TOTAL'];
  if (type === 'suppliers') totalRow.push('');
  (monthly_totals || months.map(() => 0)).forEach(total => totalRow.push(`Q${total.toFixed(2)}`));
  totalRow.push(`Q${(grand_total || 0).toFixed(2)}`, '100%');
  rows.push(totalRow);
  
  // Generar tabla
  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: 28,
    styles: {
      fontSize: 7,
      cellPadding: 1.5,
    },
    headStyles: {
      fillColor: [0, 188, 212],
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 8
    },
    footStyles: {
      fillColor: [220, 220, 220],
      textColor: 0,
      fontStyle: 'bold'
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245]
    },
    columnStyles: {
      0: { cellWidth: 35 }
    }
  });
  
  doc.save(`${filename}.pdf`);
};
